import classNames from 'classnames';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';

import { SECTIONS, SETTINGS, type Section } from '@/constants/routes';
import IdentityMenu from '@/features/identity/identity-menu';
import { PageTitleSlot } from '@/components/ui/page';

import { useClosing } from '@/utils/use-closing';

import ApiStatus from './api-status';
import AssistantCorner from '@/features/assistant/assistant-corner';

import AppUpdate from './app-update';
import ThemeToggle from './theme-toggle';

const Icon = ({ children, className = 'h-5 w-5' }: { children: ReactNode; className?: string }) => (
  <svg viewBox="0 0 24 24" className={classNames('shrink-0', className)} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {children}
  </svg>
);

const pip = (cx: number, cy: number) => <circle key={`${cx},${cy}`} cx={cx} cy={cy} r="1.3" fill="currentColor" stroke="none" />;

// One icon per section, in one line style: the sidebar, the phone's menu and its bottom tabs share them.
const SECTION_ICONS: Record<string, ReactNode> = {
  '/overview': <path d="M4 11.5L12 5l8 6.5V19a1 1 0 0 1-1 1h-4.5v-5h-5v5H5a1 1 0 0 1-1-1z" />,
  '/leads': (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 19.5c.8-3.2 3.4-5 6.5-5s5.7 1.8 6.5 5M16 4.8a3.5 3.5 0 0 1 0 6.4M18 14.6c1.8.7 3 2.4 3.5 4.9" />
    </>
  ),
  '/follow-ups': (
    <>
      <circle cx="12" cy="13" r="7.5" />
      <path d="M12 9v4.2l2.8 1.8M9.5 2.5h5" />
    </>
  ),
  '/conversations': <path d="M4.5 18.5l1.2-3.6A7.5 7.5 0 1 1 9 18.6z" />,
  // A die.
  '/casinos': (
    <>
      <rect x="4" y="4" width="16" height="16" rx="3.5" />
      {[pip(8.75, 8.75), pip(15.25, 8.75), pip(12, 12), pip(8.75, 15.25), pip(15.25, 15.25)]}
    </>
  ),
  // A pie with one slice pulled out.
  '/segments': <path d="M11 5a8 8 0 1 0 8 8h-8zM14 3a7 7 0 0 1 7 7h-7z" />,
  // A megaphone: updates sent out.
  '/messaging': <path d="M3.5 10v4a1 1 0 0 0 1 1H7l8.5 4.5v-15L7 9H4.5a1 1 0 0 0-1 1zM7.5 15.3l1 4.2H11l-.8-3.2M18.5 9.5a3.5 3.5 0 0 1 0 5" />,
  '/calls': <path d="M7.2 3.5h2.6l1.4 4.3-2 1.3a11.5 11.5 0 0 0 6 6l1.3-2 4.3 1.4v2.6a2 2 0 0 1-2.2 2A17 17 0 0 1 5.2 5.7a2 2 0 0 1 2-2.2z" />,
  '/knowledge': <path d="M5 5.5A2.5 2.5 0 0 1 7.5 3H19v14H7.5A2.5 2.5 0 0 0 5 19.5zM5 19.5A1.5 1.5 0 0 0 6.5 21H19v-4M9 7.5h6M9 11h4" />,
  '/reports': <path d="M4 20h16M7.5 16.5v-4M12 16.5v-9M16.5 16.5v-6" />,
  // Sliders.
  '/settings': (
    <>
      <path d="M4 8h9M17 8h3M4 16h3M11 16h9" />
      <circle cx="15" cy="8" r="2" />
      <circle cx="9" cy="16" r="2" />
    </>
  ),
};

// The current section is negative: white text on dark grey, for contrast. Its icon follows the text colour.
const NavItem = ({ section }: { section: Section }) => (
  <NavLink
    to={section.path}
    className={({ isActive }) =>
      classNames(
        'group flex min-h-11 items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors md:min-h-0 md:py-2',
        isActive ? 'active bg-nav-on text-white shadow-ctl' : 'text-fg-muted hover:bg-surface-hover hover:text-fg',
      )
    }
  >
    <Icon className="h-[18px] w-[18px]">{SECTION_ICONS[section.path]}</Icon>
    <span className="min-w-0 flex-1 truncate">{section.label}</span>
    {section.tag && (
      <span className="pill rounded-pill px-1.5 py-px text-[10px] text-fg-faint group-[.active]:text-white group-[.active]:[--tag-color:rgb(255_255_255/0.2)]">
        {section.tag}
      </span>
    )}
  </NavLink>
);

const Logo = ({ className }: { className: string }) => (
  <img src="/brand/prisma-logo.png" alt="" className={classNames(className, '[filter:var(--logo-filter)]')} />
);

/** What the sidebar holds. On a phone the menu drawer shows the same. The logo goes to the Overview. */
const SidebarContent = () => (
  <>
    <Link to="/overview" className="mb-6 flex items-center gap-2.5 rounded-lg px-2 text-lg font-semibold text-fg-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/50">
      <Logo className="h-8 w-8" />
      Prisma
    </Link>
    <nav aria-label="Sections" className="flex flex-col gap-1">
      {SECTIONS.map((section) => (
        <NavItem key={section.path} section={section} />
      ))}
    </nav>
    <div className="mt-auto" />
    <nav aria-label="Settings" className="mb-4 mt-4 flex flex-col">
      <NavItem section={SETTINGS} />
    </nav>
    <IdentityMenu />
    <div className="flex items-center justify-between pl-2">
      <ApiStatus />
      <ThemeToggle />
    </div>
  </>
);

const MenuIcon = () => (
  <Icon>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </Icon>
);

// Bottom tabs on a phone: the daily flows (UI-2). "More" opens the menu with the rest.
const TABS: { path: string; label: string }[] = [
  { path: '/overview', label: 'Today' },
  { path: '/leads', label: 'Leads' },
  { path: '/follow-ups', label: 'Follow-ups' },
  { path: '/conversations', label: 'Chats' },
];

const tabCls = 'flex h-14 flex-col items-center justify-center gap-0.5 text-[11px] transition-colors';

/**
 * The app frame. On a desktop: the sidebar beside the page. On a phone (below `md`,
 * UI-1, UI-2): a top bar with the section's name, bottom tabs for the daily flows,
 * and the sidebar as a drawer behind the menu button.
 */
const AppLayout = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  // The page's own title fills the top bar on a phone (PageHeader); the section's name stands in until it does.
  const [titleSlot, setTitleSlot] = useState<HTMLSpanElement | null>(null);
  const [menuClosing, closeMenu] = useClosing(useCallback(() => setMenuOpen(false), []));
  const location = useLocation();
  const current = [...SECTIONS, SETTINGS].find((s) => location.pathname === s.path || location.pathname.startsWith(s.path + '/'));

  // A new page closes the drawer.
  useEffect(() => {
    if (menuOpen) closeMenu();
    // Only a new page decides this.
  }, [location.pathname]);

  // While the drawer is open, Escape closes it and the page behind it stays put.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && closeMenu();
    window.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [menuOpen, closeMenu]);

  return (
    <div className="flex h-full">
      <aside data-app-chrome className="relative z-10 hidden w-60 shrink-0 flex-col overflow-y-auto bg-surface p-4 shadow-e2 md:flex">
        <SidebarContent />
      </aside>

      {menuOpen && (
        <div data-app-chrome className="fixed inset-0 z-50 md:hidden">
          <button
            type="button"
            aria-label="Close the menu"
            className={classNames('absolute inset-0 h-full w-full bg-overlay', menuClosing ? 'animate-fade-out' : 'animate-fade-in')}
            onClick={closeMenu}
          />
          <aside
            aria-label="Menu"
            className={classNames(
              'absolute inset-y-0 left-0 flex w-[min(85vw,300px)] flex-col overflow-y-auto bg-surface px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-[max(1rem,env(safe-area-inset-top))] shadow-e3',
              menuClosing ? 'animate-drawer-out-left' : 'animate-drawer-in-left',
            )}
            // A link closes the menu, also one to the page already open (no new page to notice).
            onClick={(e) => (e.target as HTMLElement).closest('a') && closeMenu()}
          >
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header data-app-chrome className="relative z-30 flex shrink-0 items-center gap-2 bg-surface px-2 pt-[env(safe-area-inset-top)] shadow-e2 md:hidden">
          <button
            type="button"
            aria-label="Open the menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
            className="flex h-12 w-12 items-center justify-center rounded-lg text-fg-muted hover:bg-surface-hover hover:text-fg"
          >
            <MenuIcon />
          </button>
          <Link to="/overview" aria-label="Overview" className="flex h-12 items-center rounded-lg px-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/50">
            <Logo className="h-7 w-7" />
          </Link>
          <h1 className="min-w-0 truncate text-base font-semibold text-fg-strong">
            <span ref={setTitleSlot} className="peer" />
            <span className="hidden peer-empty:inline">{current?.label ?? 'Prisma'}</span>
          </h1>
        </header>

        {/* On a phone the room for the bottom tabs is a spacer, not padding: a sticky action
            bar measures from the padding edge, so padding would leave it floating above the tabs.
            Relative, so an element placed absolutely in a page (a screen reader's label) scrolls
            with the page: it used to stretch the document, which scrolled on past the page's end
            and showed an empty gap. */}
        <PageTitleSlot.Provider value={titleSlot}>
          <main className="relative min-w-0 flex-1 overflow-y-auto overflow-x-hidden px-4 pt-4 md:overflow-x-auto md:p-8">
            <Outlet />
            <div aria-hidden className="h-[calc(5rem+env(safe-area-inset-bottom))] md:hidden" />
          </main>
        </PageTitleSlot.Provider>

        <nav aria-label="Main" data-app-chrome className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 bg-surface pb-[env(safe-area-inset-bottom)] shadow-up md:hidden">
          {TABS.map((t) => (
            <NavLink key={t.path} to={t.path} className={({ isActive }) => classNames(tabCls, isActive ? 'text-brand' : 'text-fg-muted')}>
              <Icon>{SECTION_ICONS[t.path]}</Icon>
              {t.label}
            </NavLink>
          ))}
          <button type="button" onClick={() => setMenuOpen(true)} className={classNames(tabCls, 'text-fg-muted')}>
            <MenuIcon />
            More
          </button>
        </nav>
      </div>

      <AppUpdate />
      <AssistantCorner />
    </div>
  );
};

export default AppLayout;
